// File: Components/RoomCheckOutTable.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import Select from "react-select";
import PageBody from "Common/Custom/PageBody";
import InvoicePrint from "./InvoicePrint";
import propTypes from "prop-types";
import { Link } from "react-router-dom";
import DeleteBtn from "./DeleteBtn"


const RoomCheckOutTable = ({
    RoomCkeckOutDetail = {},
    paymentMethodTypesOption = [],
    onCheckOut = () => { },
    DeleteParking,
    DeletePoolbooking,
    DeleteTransport,
    parkingBills = [],
    poolBills = [],
    transportBills = [],
    refetch = () => { }
}) => {
    const customers = RoomCkeckOutDetail?.customer_details || [];
    const roomRentDetails = RoomCkeckOutDetail?.room_rent_details || [];
    const billing_details = RoomCkeckOutDetail?.billing_details?.[0] || {};
    const credit = RoomCkeckOutDetail?.credit || [];
    const special_discount = RoomCkeckOutDetail?.special_discount || [];
    const additional_charges_data = RoomCkeckOutDetail?.additional_charges || [];
    const bill_settlement = RoomCkeckOutDetail?.bill_settlement || [];

    // =================== ROOM RENT CALCULATIONS ===================
    const room_rent_amount = roomRentDetails.reduce(
        (sum, r) => sum + Number(r.room_total || 0), 0
    );
    const tax = roomRentDetails.reduce((sum, r) => sum + Number(r.tax || 0), 0);
    const service_charge_amount = Number(billing_details?.service_charge_amount || 0);
    const advance_amount = Number(billing_details?.advance_amount || 0);

    // FIXED: Add extra services and complementary amounts from billing_details
    const extra_services_amount = Number(billing_details?.extra_services_amount || 0);
    const complementary_amount = Number(billing_details?.complementary_amount || 0);

    // =================== BILL STATES ===================
    const [offerDiscount, setOfferDiscount] = useState(Number(billing_details?.offer_discount) || "");
    const [discountReason, setDiscountReason] = useState("");

    // =================== ADDITIONAL CHARGES ===================
    const [additionalChargeAmount, setAdditionalChargeAmount] = useState("");
    const [additionalChargeRemarks, setAdditionalChargeRemarks] = useState("");

    // =================== CREDIT ===================
    const [creditType, setCreditType] = useState({ value: "no-credit", label: "No Credit" });
    const [creditAmount, setCreditAmount] = useState("");
    const [creditRemarks, setCreditRemarks] = useState("");
    const [settlement, setSettlement] = useState([{ payment_mode: "", amount: "" }]);

    // =================== SPECIAL DISCOUNT ===================
    const [specialDiscountType, setSpecialDiscountType] = useState({ value: "no-discount", label: "No Discount" });
    const [specialDiscountAmount, setSpecialDiscountAmount] = useState("");
    const [specialDiscountRemarks, setSpecialDiscountRemarks] = useState("");

    // =================== INITIAL DATA ===================
    useEffect(() => {
        const discount = Number(billing_details?.offer_discount);
        setOfferDiscount(discount || "");

        // Initialize discount reason from API
        setDiscountReason(billing_details?.discount_reason || "");

        // Initialize settlement from top-level bill_settlement
        if (bill_settlement.length > 0) {
            setSettlement(
                bill_settlement.map(s => ({
                    payment_mode: s.payment_mode || "",
                    amount: s.amount || ""
                }))
            );
        }

        if (additional_charges_data.length > 0) {
            const chargeAmt = Number(additional_charges_data[0]?.additional_charge_amount);
            setAdditionalChargeAmount(chargeAmt || "");
            setAdditionalChargeRemarks(additional_charges_data[0]?.remarks || "");
        }

        if (credit.length > 0) {
            const creditAmt = Number(credit[0]?.credit_amount);
            setCreditAmount(creditAmt || "");
            setCreditRemarks(credit[0]?.remarks || "");
        }

        if (special_discount.length > 0) {
            const spAmt = Number(special_discount[0]?.discount_amount);
            setSpecialDiscountAmount(spAmt || "");
            setSpecialDiscountRemarks(special_discount[0]?.remarks || "");
        }
    }, [additional_charges_data, credit, special_discount, billing_details, bill_settlement]);

    // =================== PAYMENT HANDLERS ===================
    const handlePaymentModeChange = (index, selected) => {
        const updated = [...settlement];
        updated[index] = { ...updated[index], payment_mode: selected ? selected.value : "" };
        setSettlement(updated);
    };

    const PostedBill = useMemo(() => [
        ...parkingBills.map(b => ({
            ...b,
            editKey: "/admin/room-check-out/edit-booking",
            deleteFn: DeleteParking
        })),
        ...poolBills.map(b => ({
            ...b,
            editKey: "/admin/room-check-out/edit-pool-booking",
            deleteFn: DeletePoolbooking
        })),
        ...transportBills.map(b => ({
            ...b,
            editKey: "/admin/transport-facility-booking/edit-vehicle-booking",
            deleteFn: DeleteTransport
        }))
    ], [parkingBills, poolBills, transportBills, DeleteParking, DeletePoolbooking, DeleteTransport]);


    const handleAmountChange = (index, value) => {
        const updated = [...settlement];
        updated[index] = { ...updated[index], amount: value };
        setSettlement(updated);
    };

    const removeSettlementRow = (index) => {
        if (settlement.length > 1) setSettlement(settlement.filter((_, i) => i !== index));
    };

    const addSettlementRow = () => setSettlement([...settlement, { payment_mode: "", amount: "" }]);

    // =================== CALCULATION FUNCTION (FIXED) ===================
    const calculateAmounts = () => {
        const _offerDiscount = Number(offerDiscount) || 0;
        const _additionalChargeAmount = Number(additionalChargeAmount) || 0;
        const _creditAmount = Number(creditAmount) || 0;
        const _specialDiscountAmount = Number(specialDiscountAmount) || 0;

        // Discount calculation
        const discountAmount = (room_rent_amount * _offerDiscount) / 100;

        const totalRoomRentAfterDiscount = room_rent_amount - discountAmount;
        const totalRoomRentWithServiceCharge =
            totalRoomRentAfterDiscount + service_charge_amount;
        const totalWithTax = totalRoomRentWithServiceCharge + tax;

        // Net payable calculation
        const netPayable =
            totalWithTax +
            extra_services_amount +
            complementary_amount -
            advance_amount +
            _additionalChargeAmount;

        const finalPayable = netPayable - _creditAmount - _specialDiscountAmount;

        const collected = settlement.reduce(
            (sum, s) => sum + Number(s.amount || 0),
            0
        );

        // Sum of posted bills (parking, etc.)
        const postedBillTotal = PostedBill.reduce(
            (sum, item) => sum + Number(item.total || 0),
            0
        );

        // Remaining amount
        const remaining = finalPayable; // keep remaining static, unchanged by payment inputs

        const change = collected > finalPayable ? collected - finalPayable : 0;

        return {
            discountAmount,
            totalRoomRentAfterDiscount,
            totalRoomRentWithServiceCharge,
            totalWithTax,
            netPayable,
            finalPayable,
            collected,
            postedBillTotal,
            remaining,
            change
        };
    };

    const {
        discountAmount,
        totalRoomRentAfterDiscount,
        totalRoomRentWithServiceCharge,
        totalWithTax,
        netPayable,
        finalPayable,
        collected,
        remaining,
        change
    } = calculateAmounts();

    // =================== OPTIONS ===================
    const creditTypeOptions = [
        { value: "no-credit", label: "No Credit" },
        { value: "admin", label: "Admin" },
        { value: "regular", label: "Regular Customer" },
    ];

    const specialDiscountOptions = [
        { value: "no-discount", label: "No Discount" },
        { value: "mdsfriend", label: "MD's Friend" },
        { value: "friend", label: "Friend" },
        { value: "regular", label: "Regular Customer" },
        { value: "ncorder", label: "NC Order" },
    ];

    const discountReasonOptions = [
        { value: "", label: "No Discount" },
        { value: "offer", label: "Offer" },
        { value: "reduction", label: "Reduction" },
        { value: "md-guest", label: "MD Guest" },
    ];

    const total_amount = roomRentDetails.reduce((sum, r) => sum + Number(r.amount_after_discount || 0), 0);
    const grand_total = roomRentDetails.reduce((sum, r) => sum + Number(r.grand_total || 0), 0);


    const handleCheckOutSubmit = () => {
        const booking_id = RoomCkeckOutDetail?.id;
        const payload = {
            booking_id: booking_id,

            // 1. Billing Info
            offer_discount_percentage: Number(offerDiscount) || 0,
            discount_reason: discountReason,
            service_charge: service_charge_amount,
            additional_charge: Number(additionalChargeAmount) || 0,
            additional_charge_remarks: additionalChargeRemarks,

            // 2. Credits
            credit_type: creditType?.value || "",
            credit_amount: Number(creditAmount) || 0,
            credit_remarks: creditRemarks,

            // 3. Special Discounts
            special_discount_type: specialDiscountType?.value || "",
            special_discount_amount: Number(specialDiscountAmount) || 0,
            special_discount_remarks: specialDiscountRemarks,

            // 4. Bill Settlements
            settlements: settlement.map(s => ({
                payment_method_id: s.payment_mode,
                amount: Number(s.amount) || 0
            })),

            // 5. Calculated Totals
            total_bill: finalPayable,
            total_paid: collected,
            balance: remaining,
            parking_booking_id: parkingBills.map(item => item.id),
            p_booking_id: poolBills.map(item => item.id),
            vehicle_booking_id: transportBills.map(item => item.id)
        };
        onCheckOut(null, payload);
    };

    // Print ref
    const printRef = useRef();

    const handleNativePrint = () => {
        const printContents = printRef.current.innerHTML;

        const printWindow = window.open("", "_blank", "width=800,height=600");

        printWindow.document.write(`<!DOCTYPE html>
          <html>
            <head>
            <title>Renbow Resort</title>
      
              <!-- Bootstrap -->
              <link rel="stylesheet" href="/assets/css/bootstrap.min.css" />
              <style>
                :root {
                  --primary: #2C3E50;
                  --secondary: #E67E22;
                  --bg: #F9FAFB;
                  --text: #333;
                  --light: #fff;
                }
                body {font-family: Arial, sans-serif; background: var(--bg); margin: 0; padding: 20px; color: var(--text);}
                .invoice-wrap {max-width: 800px; margin: 0 auto; background: var(--light); box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 8px; overflow: hidden;}
                .header {background: linear-gradient(135deg, var(--primary), var(--secondary)); color: var(--light); padding: 20px; text-align: center;}
                .header h1 {margin: 0; font-size: 24px; letter-spacing: 2px;}
                .section {padding: 20px; border-bottom: 1px solid #e0e0e0;}
                .section h2 {margin: 0 0 10px; font-size: 16px; color: var(--primary); border-bottom: 2px solid var(--secondary); display: inline-block; padding-bottom: 4px;}
                table {width: 100%; border-collapse: collapse; margin-top: 10px;}
                th, td {padding: 8px; text-align: left; border-bottom: 1px solid #e0e0e0;}
                th {background: var(--bg); font-weight: 600;}
                .totals td {font-weight: 600;}
                .footer {padding: 20px; text-align: center; font-size: 12px; color: #777;}
              </style>
            </head>
            <body>
              <div class="invoice-wrap">
                ${printContents}
              </div>
              <div class="footer">Thank you for choosing our hotel. Please visit again!</div>
            </body>
          </html>
        `);

        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    // Update the printData object to include everything from the state:
    const printData = {
        customer_details: customers.length > 0 ? customers : [],
        room_rent_details: roomRentDetails.length > 0 ? roomRentDetails : [],
        billing_details: [{
            offer_discount: offerDiscount,
            service_charge_amount: service_charge_amount,
            extra_services_amount: extra_services_amount,
            complementary_amount: complementary_amount,
            advance_amount: advance_amount
        }],
        totals: {
            room_rent_amount: room_rent_amount,
            tax: tax,
            discountAmount: discountAmount,
            additionalChargeAmount: additionalChargeAmount,
            creditAmount: creditAmount,
            specialDiscountAmount: specialDiscountAmount,
            finalPayable: finalPayable,
            collected: collected
        },
        posted_bills: PostedBill.map(b => ({
            bill_type: b.bill_type,
            total: Number(b.total)
        })),
        settlement: settlement
            .filter(s => s.payment_mode && s.amount > 0)
            .map(s => ({
                payment_mode: s.payment_mode,
                amount: Number(s.amount),
                payment_mode_name: paymentMethodTypesOption?.find(
                    m => m.payment_method_id === s.payment_mode
                )?.payment_method_name || "Cash/Other"
            }))
    };

    // =================== RENDER ===================

    return (
        <>
            {/* Hidden Print Component */}
            <div style={{
                height: "0px",
                overflow: "hidden",
                position: "absolute",
                zIndex: -1
            }}>
                <InvoicePrint ref={printRef} data={printData} />
            </div>
            <PageBody>
                {/* ================= ROOM RENT DETAILS ================= */}
                <div className="col-12" id="invoice">
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm mx-2">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan="17" className="text-center">
                                        Room Rent Details
                                    </th>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Room No</th>
                                    <th className="text-nowrap">Check In</th>
                                    <th className="text-nowrap">Check Out</th>
                                    <th className="text-nowrap">Person</th>
                                    <th className="text-nowrap">Child</th>
                                    <th className="text-nowrap">Extra Bed</th>
                                    <th className="text-nowrap">Extra Person</th>
                                    <th className="text-nowrap">Extra Child</th>
                                    <th className="text-nowrap">Room Type</th>
                                    <th className="text-nowrap">Days</th>
                                    <th className="text-nowrap">Rent/Day</th>
                                    <th className="text-nowrap">Total</th>
                                    <th className="text-nowrap">Discount</th>
                                    <th className="text-nowrap">Discount Days</th>
                                    <th className="text-nowrap">After Discount</th>
                                    <th className="text-nowrap">Tax</th>
                                    <th className="text-nowrap">Grand Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roomRentDetails.map((detail, index) => (
                                    <tr key={index}>
                                        <td>{detail.room_no}</td>
                                        <td>{detail.check_in_date}</td>
                                        <td>{detail.check_out_date}</td>
                                        <td>{detail.person}</td>
                                        <td>{detail.child}</td>
                                        <td>{detail.extra_bed || "-"}</td>
                                        <td>{detail.extra_person || "-"}</td>
                                        <td>{detail.extra_child || "-"}</td>
                                        <td>{detail.room_type}</td>
                                        <td>{detail.no_of_days}</td>
                                        <td>{Number(detail.rent_per_day || 0).toFixed(2)}</td>
                                        <td>{Number(detail.total_amount || detail.room_total || 0).toFixed(2)}</td>
                                        <td>{detail.rent_discount}%</td>
                                        <td>{Number(detail.discount_days || 0).toFixed(2)}</td>
                                        <td>{Number(detail.amount_after_discount || 0).toFixed(2)}</td>
                                        <td>{Number(detail.tax || 0).toFixed(2)}</td>
                                        <td>{Number(detail.grand_total || 0).toFixed(2)}</td>
                                    </tr>
                                ))}
                                <tr className="fw-bold bg-light">
                                    <td colSpan="14" className="text-end">Total:</td>
                                    <td>{total_amount.toFixed(2)}</td>
                                    <td>{tax.toFixed(2)}</td>
                                    <td>{grand_total.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= CUSTOMER DETAILS ================= */}
                {customers.length > 0 && (
                    <div className="col-md-6">
                        <div className="table-responsive h-100">
                            <table className="table table-bordered  table-sm mb-0 h-100">
                                <thead className="table-light">
                                    <tr>
                                        <th colSpan={2} className="text-center">
                                            Customer Details
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers.map((detail, index) => (
                                        <React.Fragment key={index}>
                                            <tr>
                                                <th className="text-nowrap" style={{ width: '40%' }}>Name</th>
                                                <td className="text-nowrap">{detail.name || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Room No</th>
                                                <td className="text-nowrap">{detail.room_no || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th>Booking No</th>
                                                <td>{detail.booking_no || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Email</th>
                                                <td className="text-nowrap">{detail.email || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Mobile No</th>
                                                <td className="text-nowrap">{detail.mobile_no || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Address</th>
                                                <td className="text-nowrap">{detail.address || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Time Format</th>
                                                <td className="text-nowrap">{detail.time_format || "N/A"}</td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Booking Type</th>
                                                <td className="text-nowrap">
                                                    <input
                                                        className="form-control form-control-sm"
                                                        value={detail.booking_type_name || "N/A"}
                                                        readOnly
                                                    />
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="text-nowrap">Booking Source</th>
                                                <td className="text-nowrap">
                                                    <input
                                                        className="form-control form-control-sm"
                                                        value={detail.booking_source_name || "N/A"}
                                                        readOnly
                                                    />
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ================= BILLING DETAILS (FIXED) ================= */}
                <div className="col-md-6">
                    <div className="table-responsive h-100">
                        <table className="table table-bordered table-sm mb-0 h-100">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={2} className="text-center">
                                        Billing Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th className="text-end text-nowrap" style={{ width: '50%' }}>Room Rent Amount</th>
                                    <td className="text-end text-nowrap">{room_rent_amount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Discount (Max-100%)</th>
                                    <td>
                                        <div className="d-flex gap-2 align-items-center flex-wrap flex-md-nowrap">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                placeholder="%"
                                                value={offerDiscount}
                                                min="0"
                                                max="100"
                                                onChange={(e) => setOfferDiscount(e.target.value)}
                                            />
                                            <select
                                                className="form-select form-select-sm"
                                                value={discountReason}
                                                onChange={(e) => setDiscountReason(e.target.value)}
                                                style={{ minWidth: '200px' }}
                                            >
                                                {discountReasonOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Discount Amount</th>
                                    <td className="text-end text-nowrap">{discountAmount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Service Charge Amount</th>
                                    <td className="text-end text-nowrap">{service_charge_amount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Total Room Rent Amount</th>
                                    <td className="text-end text-nowrap">{totalRoomRentWithServiceCharge.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Total Room Rent Amount With Tax</th>
                                    <td className="text-end text-nowrap">{totalWithTax.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Complementary Amount</th>
                                    <td className="text-end text-nowrap">{complementary_amount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Extra Bed/Person/Child Amount</th>
                                    <td className="text-end text-nowrap">{extra_services_amount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th className="text-nowrap">Advance Amount</th>
                                    <td className="text-end text-nowrap">{advance_amount.toFixed(2)}</td>
                                </tr>
                                <tr className="fw-bold">
                                    <th className="text-nowrap">Payable Rent Amount</th>
                                    <td className="text-end text-success text-nowrap">{(totalWithTax + extra_services_amount + complementary_amount - advance_amount).toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= ADDITIONAL CHARGES ================= */}
                <div className="col-md-6">
                    <div className="table-responsive mb-3">
                        <table className="table table-bordered  mb-0 table-sm">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={2} className="text-center">
                                        Additional Charges
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th style={{ width: '50%' }}>Additional Charges</th>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Enter additional charge"
                                            value={additionalChargeAmount}
                                            onChange={(e) => setAdditionalChargeAmount(e.target.value)}
                                            min="0"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Additional Charge Comments</th>
                                    <td>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows="2"
                                            value={additionalChargeRemarks}
                                            onChange={(e) => setAdditionalChargeRemarks(e.target.value)}
                                            placeholder="Enter comments..."
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ================= PAYMENT DETAILS (FIXED) ================= */}
                    <div className="table-responsive">
                        <table className="table table-bordered striped table-sm mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={2} className="text-center">
                                        Payment Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th style={{ width: '50%' }}>Net Payable Amount</th>
                                    <td className="text-end text-success">{netPayable.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Credit Amount</th>
                                    <td className="text-end">{Number(creditAmount || 0).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Special Discount Amount</th>
                                    <td className="text-end">{Number(specialDiscountAmount || 0).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Posted Bill Total</th>
                                    {/* <td className="text-end">{postedBillTotal.toFixed(2)}</td> */}
                                </tr>
                                <tr className="fw-bold">
                                    <th>Payable Amount</th>
                                    <td className="text-end text-success">{finalPayable.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= ROOM POSTED BILL ================= */}
                <div className="col-md-6">
                    <div className="table-responsive h-100">
                        <table className="table table-bordered table-sm  mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={3} className="text-center">
                                        Room Posted Bill
                                    </th>
                                </tr>
                                <tr>
                                    <th>Bill Type</th>
                                    <th>Total </th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {PostedBill?.map((item, i) => (
                                    <tr key={i}>
                                        <td>{item.bill_type}</td>
                                        <td>{item.total}</td>
                                        <td className="text-center">
                                            <Link
                                                to={`${item["editKey"]}/${item.id}`}
                                                className="btn btn-primary btn-sm border me-1 rounded-2"
                                            >
                                                {<svg xmlns="http://www.w3.org/2000/svg" width={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="svg-stroke">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
                                                    <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" />
                                                    <path d="M16 5l3 3" />
                                                </svg>}
                                            </Link>
                                            <DeleteBtn
                                                id={item.id}
                                                itemName={item.bill_type}
                                                fetchData={refetch}
                                                apiDeleteFunction={item.deleteFn}
                                            />

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= CREDIT ================= */}
                <div className="col-md-6">
                    <div className="table-responsive mb-3">
                        <table className="table table-bordered  table-sm mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={2} className="text-center">
                                        Credit
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th style={{ width: '40%' }}>Type</th>
                                    <td>
                                        <Select
                                            options={creditTypeOptions}
                                            value={creditType}
                                            onChange={setCreditType}
                                            placeholder="Select credit type"
                                            isClearable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '31px',
                                                    fontSize: '0.875rem'
                                                })
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Credit Amount </th>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Enter credit amount"
                                            value={creditAmount}
                                            onChange={(e) => setCreditAmount(e.target.value)}
                                            min="0"
                                            disabled={!creditType}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Remarks</th>
                                    <td>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows="2"
                                            value={creditRemarks}
                                            onChange={(e) => setCreditRemarks(e.target.value)}
                                            placeholder="Enter remarks..."
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ================= BILL SETTLEMENT ================= */}
                    <table className="table table-bordered  table-sm mb-0">
                        <thead className="table-light">
                            <tr>
                                <th colSpan="3" className="text-center">
                                    Bill Settlement
                                </th>
                            </tr>
                            <tr>
                                <th>Payment Mode</th>
                                <th>Amount</th>
                                <th style={{ width: '80px' }} className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {settlement.map((pay, i) => (
                                <tr key={i}>
                                    <td>
                                        <Select
                                            options={paymentMethodTypesOption?.map((method) => ({
                                                value: method.payment_method_id,
                                                label: method.payment_method_name,
                                            })) || []}
                                            value={
                                                paymentMethodTypesOption
                                                    ?.map((method) => ({
                                                        value: method.payment_method_id,
                                                        label: method.payment_method_name,
                                                    }))
                                                    .find((opt) => String(opt.value) === String(pay.payment_mode)) || null
                                            }
                                            onChange={(selected) => handlePaymentModeChange(i, selected)}
                                            placeholder="Select payment"
                                            isClearable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '31px',
                                                    fontSize: '0.875rem'
                                                })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Enter amount"
                                            value={pay.amount}
                                            onChange={(e) => handleAmountChange(i, e.target.value)}
                                            min="0"
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() => removeSettlementRow(i)}
                                            disabled={settlement.length === 1}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="svg-stroke"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M4 7l16 0"></path><path d="M10 11l0 6"></path><path d="M14 11l0 6"></path><path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path><path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path></svg>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan="3" className="text-end">
                                    <button
                                        className="btn btn-success btn-sm"
                                        onClick={addSettlementRow}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="svg-stroke"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M12 5l0 14"></path><path d="M5 12l14 0"></path></svg>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* ================= SPECIAL DISCOUNT ================= */}
                <div className="col-md-6">
                    <div className="table-responsive mb-3">
                        <table className="table table-bordered  table-sm mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan={2} className="text-center">
                                        Special Discount
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th style={{ width: '40%' }}>Type</th>
                                    <td>
                                        <Select
                                            options={specialDiscountOptions}
                                            value={specialDiscountType}
                                            onChange={setSpecialDiscountType}
                                            placeholder="Select discount type"
                                            isClearable
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    minHeight: '31px',
                                                    fontSize: '0.875rem'
                                                })
                                            }}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Discount Amount </th>
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            placeholder="Enter discount amount"
                                            value={specialDiscountAmount}
                                            onChange={(e) => setSpecialDiscountAmount(e.target.value)}
                                            min="0"
                                            disabled={!specialDiscountType}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th>Remarks</th>
                                    <td>
                                        <textarea
                                            className="form-control form-control-sm"
                                            rows="2"
                                            value={specialDiscountRemarks}
                                            onChange={(e) => setSpecialDiscountRemarks(e.target.value)}
                                            placeholder="Enter remarks..."
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ================= BALANCE DETAILS (FIXED) ================= */}
                    <div className="table-responsive ">
                        <table className="table table-bordered  table-sm">
                            <thead className="table-light">
                                <tr>
                                    <th colSpan="2" className="text-center">
                                        Balance Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th style={{ width: '50%' }}>Remain Amount</th>
                                    <td className="text-end fw-bold">{finalPayable.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Collected Amount</th>
                                    <td className="text-end">{collected.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Remaining Amount</th>
                                    <td className="text-end">{remaining.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <th>Change Amount</th>
                                    <td className="text-end">{change.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ================= ACTION BUTTONS (FIXED) ================= */}
                <div className="col-12">
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                            className="btn btn-success"
                            onClick={handleNativePrint}
                        >
                            Print Invoice
                        </button>
                        <button
                            className="btn btn-primary"
                            disabled={collected < finalPayable || settlement[0].payment_mode === ""}
                            onClick={handleCheckOutSubmit}
                        >
                            Checkout
                        </button>
                    </div>
                </div>
            </PageBody>
        </>
    );
};

RoomCheckOutTable.propTypes = {
    RoomCkeckOutDetail: propTypes.object,
    paymentMethodTypesOption: propTypes.array,
    onCheckOut: propTypes.func,
    PostedBill: propTypes.array,
    DeleteParking: propTypes.func,
    DeleteRoom: propTypes.func,
    DeleteService: propTypes.func,
    DeleteTransport: propTypes.func,
}

export default RoomCheckOutTable;
